import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Landmark, ArrowLeft, ArrowRight, Leaf, CheckCircle2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WORK_TYPES = [
  { id: "isolation_combles", label: "Isolation des combles" },
  { id: "isolation_murs", label: "Isolation des murs" },
  { id: "isolation_planchers", label: "Isolation des planchers" },
  { id: "pompe_chaleur", label: "Pompe à chaleur" },
  { id: "chaudiere", label: "Chaudière performante" },
  { id: "fenetres", label: "Fenêtres / Menuiseries" },
  { id: "ventilation", label: "Ventilation (VMC)" },
  { id: "renovation_globale", label: "Rénovation globale" },
  { id: "eclairage_led", label: "Éclairage LED public" },
  { id: "gestion_energie", label: "Gestion de l'énergie (GTB/GTC)" }
];

const COLLECTIVITY_TYPES = [
  { value: "commune", label: "Commune" },
  { value: "intercommunalite", label: "Intercommunalité (EPCI)" },
  { value: "departement", label: "Département" },
  { value: "region", label: "Région" },
  { value: "autre", label: "Autre établissement public" }
];

const PROJECT_PHASES = [
  { value: "etude", label: "Étude / Réflexion" },
  { value: "planification", label: "Planification" },
  { value: "appel_offre", label: "Appel d'offres" },
  { value: "en_cours", label: "Travaux en cours" }
];

export default function CollectiviteForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    beneficiary_type: "collectivite",
    // Collectivité info
    collectivity_name: "",
    collectivity_type: "",
    population: "",
    // Contact
    contact_first_name: "",
    contact_last_name: "",
    contact_role: "",
    email: "",
    phone: "",
    // Adresse
    address: "",
    postal_code: "",
    city: "",
    department: "",
    // Bâtiment
    housing_type: "batiment",
    housing_age: "",
    housing_surface: "",
    // Travaux
    work_types: [],
    // Projet
    urgency: "6_mois",
    project_phase: "etude",
    has_approved_budget: false,
    additional_info: ""
  });

  const totalSteps = 5;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleWorkType = (workId) => {
    setFormData(prev => ({
      ...prev,
      work_types: prev.work_types.includes(workId)
        ? prev.work_types.filter(w => w !== workId)
        : [...prev.work_types, workId]
    }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.collectivity_name || !formData.collectivity_type) {
          toast.error("Veuillez remplir le nom et le type de collectivité");
          return false;
        }
        return true;
      case 2:
        if (!formData.contact_first_name || !formData.contact_last_name || !formData.contact_role || !formData.email || !formData.phone) {
          toast.error("Veuillez remplir tous les champs de contact");
          return false;
        }
        return true;
      case 3:
        if (!formData.address || !formData.postal_code || !formData.city || !formData.department) {
          toast.error("Veuillez remplir l'adresse complète du bâtiment");
          return false;
        }
        return true;
      case 4:
        if (!formData.housing_age || !formData.housing_surface) {
          toast.error("Veuillez renseigner l'âge et la surface du bâtiment");
          return false;
        }
        if (formData.work_types.length === 0) {
          toast.error("Veuillez sélectionner au moins un type de travaux");
          return false;
        }
        return true;
      case 5:
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        housing_age: parseInt(formData.housing_age),
        housing_surface: parseInt(formData.housing_surface),
        population: formData.population ? parseInt(formData.population) : null
      };

      const response = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur lors de la soumission");
      }

      const lead = await response.json();
      toast.success("Demande enregistrée avec succès !");
      navigate(`/resultat/${lead.id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Navbar */}
      <nav className="navbar flex items-center justify-between" data-testid="navbar">
        <Link to="/" className="flex items-center gap-2">
          <Leaf className="h-8 w-8 text-[#1A4D2E]" />
          <span className="text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            MesSubventions
          </span>
        </Link>
        <div className="flex items-center gap-2 text-[#1A4D2E]">
          <Landmark className="h-5 w-5" />
          <span className="font-medium">Collectivité</span>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4, 5].map(s => (
              <div 
                key={s} 
                className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all
                  ${s < step ? 'bg-[#1A4D2E] text-white' : 
                    s === step ? 'bg-[#1A4D2E] text-white ring-4 ring-[#1A4D2E]/20' : 
                    'bg-slate-200 text-slate-500'}`}
              >
                {s < step ? <CheckCircle2 className="h-5 w-5" /> : s}
              </div>
            ))}
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#1A4D2E] transition-all duration-300"
              style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="collectivite-form">
          {/* Step 1: Collectivité Info */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Votre collectivité
                </h2>
                <p className="text-slate-600">Informations sur votre établissement</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="collectivity_name">Nom de la collectivité *</Label>
                  <Input
                    id="collectivity_name"
                    value={formData.collectivity_name}
                    onChange={e => updateField("collectivity_name", e.target.value)}
                    placeholder="Ex: Commune de Lyon"
                    data-testid="collectivity-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="collectivity_type">Type de collectivité *</Label>
                  <Select value={formData.collectivity_type} onValueChange={v => updateField("collectivity_type", v)}>
                    <SelectTrigger data-testid="collectivity-type-select">
                      <SelectValue placeholder="Sélectionnez un type" />
                    </SelectTrigger>
                    <SelectContent>
                      {COLLECTIVITY_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="population">Population (habitants)</Label>
                  <Input
                    id="population"
                    type="number"
                    value={formData.population}
                    onChange={e => updateField("population", e.target.value)}
                    placeholder="Ex: 50000"
                    data-testid="population-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Contact */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Contact projet
                </h2>
                <p className="text-slate-600">Personne en charge du projet de rénovation</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_first_name">Prénom *</Label>
                    <Input
                      id="contact_first_name"
                      value={formData.contact_first_name}
                      onChange={e => updateField("contact_first_name", e.target.value)}
                      placeholder="Prénom"
                      data-testid="contact-firstname-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_last_name">Nom *</Label>
                    <Input
                      id="contact_last_name"
                      value={formData.contact_last_name}
                      onChange={e => updateField("contact_last_name", e.target.value)}
                      placeholder="Nom"
                      data-testid="contact-lastname-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact_role">Fonction *</Label>
                  <Input
                    id="contact_role"
                    value={formData.contact_role}
                    onChange={e => updateField("contact_role", e.target.value)}
                    placeholder="Ex: Directeur des Services Techniques"
                    data-testid="contact-role-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email professionnel *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={e => updateField("email", e.target.value)}
                    placeholder="contact@collectivite.fr"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={e => updateField("phone", e.target.value)}
                    placeholder="04 72 XX XX XX"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Adresse */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Localisation du bâtiment
                </h2>
                <p className="text-slate-600">Adresse du bâtiment public à rénover</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="address">Adresse *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={e => updateField("address", e.target.value)}
                    placeholder="Numéro et nom de rue"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Code postal *</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={e => updateField("postal_code", e.target.value.replace(/\D/g, '').slice(0, 5))}
                      placeholder="69001"
                      maxLength={5}
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Ville *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={e => updateField("city", e.target.value)}
                      placeholder="Lyon"
                      data-testid="city-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="department">Département *</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={e => updateField("department", e.target.value.slice(0, 3))}
                    placeholder="69"
                    maxLength={3}
                    data-testid="department-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Bâtiment et Travaux */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Bâtiment et travaux
                </h2>
                <p className="text-slate-600">Caractéristiques du bâtiment et travaux envisagés</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="housing_type">Type de bâtiment *</Label>
                  <Select value={formData.housing_type} onValueChange={v => updateField("housing_type", v)}>
                    <SelectTrigger data-testid="housing-type-select">
                      <SelectValue placeholder="Type de bâtiment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="batiment">Bâtiment administratif / Mairie</SelectItem>
                      <SelectItem value="local">École / Établissement scolaire</SelectItem>
                      <SelectItem value="maison">Équipement sportif / Culturel</SelectItem>
                      <SelectItem value="appartement">Logement social</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="housing_age">Âge du bâtiment (années) *</Label>
                    <Input
                      id="housing_age"
                      type="number"
                      value={formData.housing_age}
                      onChange={e => updateField("housing_age", e.target.value)}
                      placeholder="Ex: 50"
                      data-testid="housing-age-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="housing_surface">Surface (m²) *</Label>
                    <Input
                      id="housing_surface"
                      type="number"
                      value={formData.housing_surface}
                      onChange={e => updateField("housing_surface", e.target.value)}
                      placeholder="Ex: 2000"
                      data-testid="housing-surface-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-3 block">Types de travaux envisagés *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {WORK_TYPES.map(work => (
                      <div
                        key={work.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                          ${formData.work_types.includes(work.id)
                            ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                            : 'border-slate-200 hover:border-slate-300'
                          }`}
                        onClick={() => toggleWorkType(work.id)}
                        data-testid={`work-type-${work.id}`}
                      >
                        <Checkbox
                          checked={formData.work_types.includes(work.id)}
                          className="pointer-events-none"
                        />
                        <span className="text-sm">{work.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Projet */}
          {step === 5 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Votre projet
                </h2>
                <p className="text-slate-600">État d'avancement et informations complémentaires</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="project_phase">Phase du projet *</Label>
                  <Select value={formData.project_phase} onValueChange={v => updateField("project_phase", v)}>
                    <SelectTrigger data-testid="project-phase-select">
                      <SelectValue placeholder="Sélectionnez une phase" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_PHASES.map(phase => (
                        <SelectItem key={phase.value} value={phase.value}>{phase.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="urgency">Délai de réalisation souhaité *</Label>
                  <Select value={formData.urgency} onValueChange={v => updateField("urgency", v)}>
                    <SelectTrigger data-testid="urgency-select">
                      <SelectValue placeholder="Sélectionnez un délai" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediat">Immédiat (moins d'1 mois)</SelectItem>
                      <SelectItem value="3_mois">Dans les 3 prochains mois</SelectItem>
                      <SelectItem value="6_mois">Dans les 6 prochains mois</SelectItem>
                      <SelectItem value="plus_tard">Plus tard / En réflexion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${formData.has_approved_budget
                      ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                      : 'border-slate-200 hover:border-slate-300'
                    }`}
                  onClick={() => updateField("has_approved_budget", !formData.has_approved_budget)}
                  data-testid="has-approved-budget-checkbox"
                >
                  <Checkbox
                    checked={formData.has_approved_budget}
                    className="pointer-events-none"
                  />
                  <div>
                    <span className="font-medium">Budget voté</span>
                    <p className="text-sm text-slate-500">Le budget pour ce projet a été voté par l'assemblée délibérante</p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="additional_info">Informations complémentaires</Label>
                  <Textarea
                    id="additional_info"
                    value={formData.additional_info}
                    onChange={e => updateField("additional_info", e.target.value)}
                    placeholder="Précisions sur votre projet, objectifs de performance énergétique..."
                    rows={4}
                    data-testid="additional-info-textarea"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={prevStep}
                className="flex items-center gap-2"
                data-testid="prev-step-btn"
              >
                <ArrowLeft className="h-4 w-4" />
                Précédent
              </Button>
            ) : (
              <Link to="/">
                <Button variant="outline" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Retour
                </Button>
              </Link>
            )}

            {step < totalSteps ? (
              <Button
                onClick={nextStep}
                className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 flex items-center gap-2"
                data-testid="next-step-btn"
              >
                Suivant
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 flex items-center gap-2"
                data-testid="submit-btn"
              >
                {loading ? "Envoi en cours..." : "Calculer mon éligibilité"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
